import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, Typography, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Box, IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { ref, remove, update } from 'firebase/database';
import { database as db } from './../firebaseConfig';
import { YEAR } from '../config';
import { bookedSetsDelta } from '../inventory';
import { getConfirmationName, describeCartSets, confirmationRound } from '../bookings';
import { fetchConfirmations } from '../adminData';

function confirmationCreatedAt(conf) {
  if (conf.createdAt) return conf.createdAt;
  if (conf.create_time) return new Date(conf.create_time).getTime();
  return null;
}

export default function AllCartsDialog({ open, onClose }) {
  const [confirmations, setConfirmations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUID, setSelectedUID] = useState('all');
  const [selectedRound, setSelectedRound] = useState('all');

  const nameMap = {};
  confirmations.forEach(conf => {
    const name = getConfirmationName(conf);
    nameMap[conf.uid] = name;
  });

  const uniqueUIDs = Array.from(new Set(confirmations.map(conf => conf.uid)))
    .sort((a, b) => nameMap[a].localeCompare(nameMap[b]));

  const uniqueRounds = Array.from(
    new Set(confirmations.map(confirmationRound)),
  ).sort((a, b) => a - b);

  const filteredConfirmations = confirmations
    .filter(conf => selectedUID === 'all' || conf.uid === selectedUID)
    .filter(
      conf => selectedRound === 'all' || confirmationRound(conf) === selectedRound,
    );

  const scopeLabel =
    selectedRound === 'all' ? 'All rounds' : `Round ${selectedRound}`;

  useEffect(() => {
    if (open) {
      const fetchAllConfirmations = async () => {
        setLoading(true);
        try {
          setConfirmations(await fetchConfirmations(db));
        } catch (error) {
          console.error('Error loading confirmations:', error);
          setConfirmations([]);
        } finally {
          setLoading(false);
        }
      };

      fetchAllConfirmations();
    }
  }, [open]);

  // Keep the per-day availability counters in sync; best-effort so the
  // admin action itself never fails on a counter problem.
  const adjustCounters = async (conf, sign) => {
    try {
      const delta = bookedSetsDelta(conf.cart, sign);
      if (Object.keys(delta).length) await update(ref(db), delta);
    } catch (err) {
      console.warn('Failed to update availability counters:', err);
    }
  };

  const handleDelete = async (conf) => {
    if (window.confirm('Are you sure you want to delete this confirmation?')) {
      try {
        await remove(ref(db, `users/${conf.uid}/confirmations${YEAR}/${conf.id}`));
        if (conf.status !== 'CANCELLED') await adjustCounters(conf, -1);
        setConfirmations(prev => prev.filter(c => !(c.uid === conf.uid && c.id === conf.id)));
      } catch (err) {
        console.error('Failed to delete:', err);
      }
    }
  };

  const handleDownloadPdf = async () => {
    // jsPDF is heavy, so it loads only when the report is requested.
    const { downloadBookingsPdf } = await import('../report');
    downloadBookingsPdf(filteredConfirmations, scopeLabel);
  };

  const handleStatusChange = async (conf, newStatus) => {
    try {
      const updateRef = ref(db, `users/${conf.uid}/confirmations${YEAR}/${conf.id}`);
      await update(updateRef, { status: newStatus });
      if (conf.status !== 'CANCELLED' && newStatus === 'CANCELLED') {
        await adjustCounters(conf, -1);
      } else if (conf.status === 'CANCELLED' && newStatus !== 'CANCELLED') {
        await adjustCounters(conf, +1);
      }
      setConfirmations(prev =>
        prev.map(c =>
          c.uid === conf.uid && c.id === conf.id ? { ...c, status: newStatus } : c
        )
      );
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>All Checkout Confirmations</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <CircularProgress />
        ) : confirmations.length === 0 ? (
          <Typography>No confirmations found.</Typography>
        ) : (
          <>
            <Box mb={2} display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Customer Name</InputLabel>
                <Select
                  value={selectedUID}
                  label="Customer Name"
                  onChange={(e) => setSelectedUID(e.target.value)}
                >
                  <MenuItem value="all">All Customers</MenuItem>
                  {uniqueUIDs.map(uid => (
                    <MenuItem key={uid} value={uid}>
                      {nameMap[uid]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 170 }}>
                <InputLabel>Booking Round</InputLabel>
                <Select
                  value={selectedRound}
                  label="Booking Round"
                  onChange={(e) => setSelectedRound(e.target.value)}
                >
                  <MenuItem value="all">All Rounds</MenuItem>
                  {uniqueRounds.map(round => (
                    <MenuItem key={round} value={round}>
                      Round {round}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <List dense>
              {filteredConfirmations.map(conf => (
                <ListItem
                  key={conf.id}
                  divider
                  alignItems="flex-start"
                  secondaryAction={
                    <IconButton onClick={() => handleDelete(conf)} edge="end" color="error">
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <Box width="100%">
                    <Typography variant="body2">
                      <strong>Name:</strong> {getConfirmationName(conf)}{' '}
                      <em>(round {confirmationRound(conf)})</em><br />
                      <strong>User ID:</strong> {conf.uid}<br />
                      {conf.payer ? (
                        <>
                          <strong>Email:</strong> {conf.payer.email_address}<br />
                          <strong>Total:</strong> ${conf.purchase_units?.[0]?.amount?.value}<br />
                        </>
                      ) : (
                        <>
                          <strong>Email:</strong> <em>Not provided</em><br />
                          <strong>Total:</strong> ${conf.total}<br />
                        </>
                      )}
                      <strong>Sets:</strong>{' '}
                      {describeCartSets(conf.cart) || <em>none</em>}<br />
                      {confirmationCreatedAt(conf) && (
                        <>
                          <strong>Created:</strong>{' '}
                          {new Date(confirmationCreatedAt(conf)).toLocaleString()}<br />
                        </>
                      )}
                      <strong>Status:</strong>{' '}
                      <Select
                        value={conf.status}
                        onChange={(e) => handleStatusChange(conf, e.target.value)}
                        size="small"
                        sx={{ width: 120, fontWeight: 'bold', color: conf.status === 'PAID' ? 'green' : 'orange' }}
                      >
                        <MenuItem value="PAID">PAID</MenuItem>
                        <MenuItem value="PENDING">PENDING</MenuItem>
                        <MenuItem value="CANCELLED">CANCELLED</MenuItem>
                      </Select>
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<PictureAsPdfIcon />}
          onClick={handleDownloadPdf}
          disabled={loading || filteredConfirmations.length === 0}
        >
          PDF Report
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
